import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, ShieldCheck, HardHat, BadgeCheck, Award, CheckCircle2, MapPin } from "lucide-react";
import { Reveal, MaskedLines } from "../components/Reveal";
import { PartnersMarquee } from "../components/PartnersMarquee";
import { StatCounter } from "../components/StatCounter";
import { CTASection } from "../components/CTASection";
import { SEO } from "../components/SEO";
import {
  IMAGES, MISSION, VISION, QUALITY, SAFETY, WHY_CHOOSE, SERVICES,
  STATS, CERTIFICATIONS, AREAS, PROCESS, COMPANY,
} from "../data/content";

const certIcons = [ShieldCheck, BadgeCheck, HardHat, Award, CheckCircle2];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.heroMain} alt="Luxury architectural residence at dusk" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/70 via-charcoal/40 to-charcoal/80" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative h-full container-px flex flex-col justify-end pb-16 sm:pb-24">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="label text-gold mb-6"
        >
          Construction · Victoria & South Australia
        </motion.p>

        <h1 className="font-display font-light text-hero text-white max-w-5xl">
          <MaskedLines lines={["Crafting spaces", "that stand the", "test of time."]} />
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7 }}
          className="mt-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-8"
        >
          <p className="max-w-md text-lg sm:text-xl text-white/70 leading-relaxed">
            Exceptional construction solutions built on quality craftsmanship, honest communication, and
            meticulous attention to detail.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact"
              data-testid="hero-cta-quote"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 font-semibold text-forest-dark transition-transform hover:-translate-y-0.5"
            >
              Request a Quote
              <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-services"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 px-8 py-4 font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white hover:text-forest"
            >
              Our Services
            </Link>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default function Home() {
  return (
    <div>
      <SEO
        title="Kotam Projects | Premium Home Builders in Melbourne & Adelaide"
        description="Kotam Projects builds premium custom homes, renovations and extensions across Victoria and South Australia — from Melbourne and Craigieburn to Adelaide and Pooraka. Quality craftsmanship, safety-first, on time and on budget."
        path="/"
      />
      <Hero />

      {/* Company introduction */}
      <section className="container-px py-24 sm:py-32 lg:py-40">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-20 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="label text-gold">Who We Are</p>
              <h2 className="mt-6 font-display font-light text-h2 text-charcoal">
                A trusted construction partner, building lasting relationships.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 text-lg sm:text-xl text-charcoal/60 leading-relaxed">
                From planning and design through to completion, Kotam Projects is committed to building
                spaces that exceed expectations while upholding the highest standards of safety,
                professionalism, and reliability.
              </p>
              <p className="mt-6 text-lg text-charcoal/60 leading-relaxed">
                Every project is approached with integrity — ensuring our clients receive outstanding
                results delivered on time and within budget.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <Link
                to="/about"
                data-testid="home-about-link"
                className="mt-10 group inline-flex items-center gap-2 font-semibold text-forest"
              >
                Discover our story
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.15}>
              <div className="grid grid-cols-2 gap-4 sm:gap-6">
                <img src={IMAGES.facade} alt="Modern residential facade" className="rounded-[20px] h-64 sm:h-80 w-full object-cover" loading="lazy" />
                <img src={IMAGES.interior1} alt="Premium interior finish" className="rounded-[20px] h-64 sm:h-80 w-full object-cover mt-8 sm:mt-12" loading="lazy" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Trusted partners */}
      <section className="bg-concrete py-20 sm:py-28" data-testid="partners-section">
        <div className="container-px mb-12">
          <Reveal>
            <p className="label text-gold text-center">Trusted Partners</p>
            <h2 className="mt-4 text-center font-display font-light text-3xl sm:text-4xl text-charcoal">
              Building with the best in the industry
            </h2>
          </Reveal>
        </div>
        <PartnersMarquee />
      </section>

      {/* Services overview */}
      <section className="container-px py-24 sm:py-32 lg:py-40" data-testid="services-overview">
        <Reveal>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <div>
              <p className="label text-gold">What We Do</p>
              <h2 className="mt-6 font-display font-light text-h2 text-charcoal max-w-2xl">
                Comprehensive construction services
              </h2>
            </div>
            <Link to="/services" data-testid="services-view-all" className="group inline-flex items-center gap-2 font-semibold text-forest shrink-0">
              View all services <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>

        <div className="mt-16 grid gap-6 sm:grid-cols-2">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.08}>
              <Link to="/services" className="group block overflow-hidden rounded-[20px] border border-border bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                <div className="relative h-64 sm:h-72 overflow-hidden">
                  <img src={IMAGES[s.image]} alt={s.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 to-transparent" />
                  <span className="absolute bottom-5 left-6 label text-white/80">0{i + 1}</span>
                </div>
                <div className="p-8">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-2xl font-medium text-charcoal">{s.title}</h3>
                    <ArrowUpRight className="h-6 w-6 text-forest transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </div>
                  <p className="mt-4 text-charcoal/60 leading-relaxed">{s.overview}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Why choose */}
      <section className="bg-charcoal text-white py-24 sm:py-32 lg:py-40" data-testid="why-choose-section">
        <div className="container-px">
          <Reveal>
            <p className="label text-gold">Why Choose Kotam Projects</p>
            <h2 className="mt-6 font-display font-light text-h2 max-w-2xl">
              Precision, reliability, and a relentless pursuit of excellence.
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-px overflow-hidden rounded-[24px] bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_CHOOSE.map((w, i) => (
              <Reveal key={w.title} delay={(i % 3) * 0.08} className="bg-charcoal p-8 sm:p-10">
                <span className="font-display text-gold text-lg">0{i + 1}</span>
                <h3 className="mt-4 font-display text-xl font-medium">{w.title}</h3>
                <p className="mt-3 text-white/60 leading-relaxed">{w.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process preview */}
      <section className="container-px py-24 sm:py-32 lg:py-40">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="label text-gold">Our Process</p>
              <h2 className="mt-6 font-display font-light text-h2 text-charcoal">
                A clear path from concept to handover.
              </h2>
              <p className="mt-8 text-lg text-charcoal/60 leading-relaxed">
                Seven considered stages ensure every project is delivered with clarity, quality, and
                confidence — with you informed every step of the way.
              </p>
              <Link to="/process" data-testid="home-process-link" className="mt-10 group inline-flex items-center gap-2 font-semibold text-forest">
                See the full process <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <div className="grid gap-4 sm:grid-cols-2">
              {PROCESS.slice(0, 4).map((p, i) => (
                <Reveal key={p.step} delay={i * 0.08} className="rounded-[20px] border border-border bg-concrete/40 p-8">
                  <span className="font-display text-4xl font-light text-gold">{p.step}</span>
                  <h3 className="mt-4 font-display text-xl font-medium text-charcoal">{p.title}</h3>
                  <p className="mt-2 text-charcoal/60 leading-relaxed">{p.text}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="relative overflow-hidden bg-forest text-white py-24 sm:py-32 lg:py-40" data-testid="mission-vision-section">
        <div className="absolute right-0 top-0 h-full w-1/3 opacity-20">
          <img src={IMAGES.concreteDetail} alt="Construction craftsmanship detail on a Kotam Projects site" className="h-full w-full object-cover" loading="lazy" />
        </div>
        <div className="container-px relative grid gap-16 lg:grid-cols-2">
          <Reveal>
            <span className="font-display text-6xl font-light text-gold/40">01</span>
            <p className="label text-gold mt-4">Our Mission</p>
            <p className="mt-6 font-display text-2xl sm:text-3xl font-light leading-snug">{MISSION}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <span className="font-display text-6xl font-light text-gold/40">02</span>
            <p className="label text-gold mt-4">Our Vision</p>
            <p className="mt-6 font-display text-2xl sm:text-3xl font-light leading-snug">{VISION}</p>
          </Reveal>
        </div>
      </section>

      {/* Quality & Safety */}
      <section className="container-px py-24 sm:py-32 lg:py-40">
        <div className="grid gap-6 lg:grid-cols-2">
          <Reveal className="relative overflow-hidden rounded-[24px] border border-border">
            <img src={IMAGES.rebar} alt="Construction craftsmanship" className="absolute inset-0 h-full w-full object-cover opacity-10" loading="lazy" />
            <div className="relative p-10 sm:p-14">
              <p className="label text-gold">Commitment to Quality</p>
              <h3 className="mt-6 font-display text-3xl font-light text-charcoal">The foundation of every project</h3>
              <p className="mt-6 text-lg text-charcoal/60 leading-relaxed">{QUALITY}</p>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="relative overflow-hidden rounded-[24px] bg-forest text-white">
            <img src={IMAGES.worker} alt="Safety on site" className="absolute inset-0 h-full w-full object-cover opacity-15" loading="lazy" />
            <div className="relative p-10 sm:p-14">
              <p className="label text-gold">Safety First Culture</p>
              <h3 className="mt-6 font-display text-3xl font-light">More than a requirement — it's who we are</h3>
              <p className="mt-6 text-lg text-white/70 leading-relaxed">{SAFETY}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Certifications */}
      <section className="bg-concrete py-24 sm:py-32" data-testid="certifications-section">
        <div className="container-px">
          <Reveal>
            <p className="label text-gold">Certifications & Standards</p>
            <h2 className="mt-6 font-display font-light text-h2 text-charcoal max-w-2xl">
              Accredited, compliant, and built to Australian Standards.
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
            {CERTIFICATIONS.map((c, i) => {
              const Icon = certIcons[i];
              return (
                <Reveal key={c.title} delay={i * 0.06} className="rounded-[20px] border border-border bg-white p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                  <Icon className="h-8 w-8 text-forest" strokeWidth={1.5} />
                  <h3 className="mt-5 font-display text-lg font-semibold text-charcoal">{c.title}</h3>
                  <p className="mt-2 text-sm text-charcoal/60 leading-relaxed">{c.text}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Areas We Service */}
      <section className="container-px py-24 sm:py-32 lg:py-40" data-testid="areas-section">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="label text-gold">Areas We Service</p>
              <h2 className="mt-6 font-display font-light text-h2 text-charcoal">Two states. Countless communities.</h2>
              <div className="mt-8 overflow-hidden rounded-[20px]">
                <img src={IMAGES.aerial} alt="Aerial view of residential development" className="h-64 w-full object-cover" loading="lazy" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-8 grid gap-12 sm:grid-cols-2">
            <Reveal delay={0.1}>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-gold" />
                <h3 className="font-display text-2xl font-medium text-charcoal">Victoria</h3>
              </div>
              <ul className="mt-6 grid grid-cols-2 gap-y-3 gap-x-4">
                {AREAS.victoria.map((a) => (
                  <li key={a} className="text-charcoal/60 border-b border-border/70 pb-2">{a}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-gold" />
                <h3 className="font-display text-2xl font-medium text-charcoal">South Australia</h3>
              </div>
              <ul className="mt-6 grid grid-cols-2 gap-y-3 gap-x-4">
                {AREAS.southAustralia.map((a) => (
                  <li key={a} className="text-charcoal/60 border-b border-border/70 pb-2">{a}</li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="bg-charcoal py-24 sm:py-32" data-testid="stats-section">
        <div className="container-px grid gap-12 sm:grid-cols-2 lg:grid-cols-4">
          {STATS.map((s) => (
            <StatCounter key={s.label} value={s.value} suffix={s.suffix} label={s.label} />
          ))}
        </div>
      </section>

      <CTASection />
    </div>
  );
}
